import { SelectProps } from "antd";
import {
  TemplatesTypeEnum,
  CampaignTypeEnum,
  ChannelEnum,
  RFMPeriod,
  StatusEnum,
  TypeEnum,
} from "./enums";
import { ChannelKey, StatusKey, TypeKey, RfmOption } from "./type";
import { ReactNode } from "react";

// Campaign Channel
export const ChannelMap: Record<ChannelKey, ChannelEnum> = {
  SMS: ChannelEnum.Sms,
  "WhatsApp Standard": ChannelEnum.WhatsAppStandard,
  "WhatsApp Premium": ChannelEnum.WhatsAppPremium,
  Email: ChannelEnum.Email,
};

export const ChannelKeyMap: Record<ChannelEnum, ChannelKey> = {
  [ChannelEnum.Sms]: "SMS",
  [ChannelEnum.WhatsAppStandard]: "WhatsApp Standard",
  [ChannelEnum.WhatsAppPremium]: "WhatsApp Premium",
  [ChannelEnum.Email]: "Email",
};

// Campaign Type
export const TypeMap: Record<TypeKey, TypeEnum> = {
  "One-Time": TypeEnum.Onetime,
  "Recurring - Weekly": TypeEnum.Recurring_Weekly,
  "Recurring - Monthly": TypeEnum.Recurring_Monthly,
};

export const TypeKeyMap: Record<TypeEnum, TypeKey> = {
  [TypeEnum.Onetime]: "One-Time",
  [TypeEnum.Recurring_Weekly]: "Recurring - Weekly",
  [TypeEnum.Recurring_Monthly]: "Recurring - Monthly",
};

// Campaign Status
export const StatusMap: Record<StatusKey, StatusEnum> = {
  Draft: StatusEnum.Draft,
  Active: StatusEnum.Active,
  Paused: StatusEnum.Paused,
  Completed: StatusEnum.Completed,
  Cancelled: StatusEnum.Cancelled,
};

export const StatusKeyMap: Record<StatusEnum, StatusKey> = {
  [StatusEnum.Draft]: "Draft",
  [StatusEnum.Active]: "Active",
  [StatusEnum.Paused]: "Paused",
  [StatusEnum.Completed]: "Completed",
  [StatusEnum.Cancelled]: "Cancelled",
};

export const RFM_LABELS = [
  "Lifetime",
  "Last 2 Years",
  "Last 1 Year",
  "Last 9 Months",
  "Last 6 Months",
  "Last 3 Months",
] as const;
export type RFMLabel = (typeof RFM_LABELS)[number];

export const labelToRFMTPeriodMap = {
  Lifetime: RFMPeriod.Lifetime,
  "Last 2 Years": RFMPeriod.Last2Years,
  "Last 1 Year": RFMPeriod.Last1Year,
  "Last 9 Months": RFMPeriod.Last9Months,
  "Last 6 Months": RFMPeriod.Last6Months,
  "Last 3 Months": RFMPeriod.Last3Months,
} as const satisfies Record<RFMLabel, RFMPeriod>;
export const rfmPeriodToLabel: Partial<Record<RFMPeriod, RFMLabel>> = {
  [RFMPeriod.Lifetime]: "Lifetime",
  [RFMPeriod.Last2Years]: "Last 2 Years",
  [RFMPeriod.Last1Year]: "Last 1 Year",
  [RFMPeriod.Last9Months]: "Last 9 Months",
  [RFMPeriod.Last6Months]: "Last 6 Months",
  [RFMPeriod.Last3Months]: "Last 3 Months",
  // CustomRange omitted on purpose
};
export const RfmPeriod: RfmOption[] = Object.entries(labelToRFMTPeriodMap).map(
  ([label, value]) => ({ label, value }),
);

export const STATUS_ENUM_VALUES: StatusEnum[] = (
  Object.values(StatusEnum) as Array<string | number>
).filter(
  (v) =>
    typeof v === "number" ||
    !Object.prototype.hasOwnProperty.call(StatusEnum, v),
) as StatusEnum[];

export const STATUS_OPTIONS = STATUS_ENUM_VALUES.map((s) => ({
  label: StatusKeyMap[s],
  value: StatusKeyMap[s],
}));

export const CampaignTypeLabel: Record<CampaignTypeEnum, string> = {
  [CampaignTypeEnum.Voucher]: "Voucher - purchases made using vouchers only",
  [CampaignTypeEnum.Point]: "Point - purchases made using points only",
  [CampaignTypeEnum.VoucherPoint]:
    "Voucher & Point - purchases made using either vouchers or points",
  [CampaignTypeEnum.EVoucher]:
    "E-Voucher - account specific E-Voucher purchases",
  [CampaignTypeEnum.Custom]: "Custom Campaign",
};

export const CAMPAIGN_TYPE_OPTIONS: SelectProps["options"] = [
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Voucher],
    value: CampaignTypeEnum.Voucher,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Point],
    value: CampaignTypeEnum.Point,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.VoucherPoint],
    value: CampaignTypeEnum.VoucherPoint,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.EVoucher],
    value: CampaignTypeEnum.EVoucher,
  },
  {
    label: CampaignTypeLabel[CampaignTypeEnum.Custom],
    value: CampaignTypeEnum.Custom,
  },
];

export const CAMPAIGN_TEMPLATES_OPTIONS: SelectProps["options"] = [
  {
    label: "Default Templates",
    value: TemplatesTypeEnum.Default,
  },
  {
    label: "Customize",
    value: TemplatesTypeEnum.Custom,
  },
];

export function getTemplateLabel(
  value: TemplatesTypeEnum | undefined,
): ReactNode {
  const option = CAMPAIGN_TEMPLATES_OPTIONS?.find((opt) => opt.value === value);
  return option?.label || "-";
}
